import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckCircle } from 'lucide-react';

const SavingsModalitiesSection = () => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [monto, setMonto] = React.useState('');
  const [frecuencia, setFrecuencia] = React.useState('mensual');
  const [tiempo, setTiempo] = React.useState(12);

  return (
    <section id="ahorro-con-proposito" ref={ref} className="relative py-32 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-orange-500 mb-6 leading-tight pb-2">
            Personaliza al máximo tu plan.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Elije Monto, Frecuencia de pago y Tiempo.
          </p>
        </motion.div>

        {/* Interactive Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-2xl mx-auto mb-16 bg-white rounded-2xl shadow-xl border-4 border-[#fbb040] overflow-hidden"
        >
          <div className="bg-gradient-to-r from-[#fbb040] to-yellow-500 p-4 text-center">
            <h3 className="text-xl md:text-2xl font-black text-black">
              Configura tu plan personalizado
            </h3>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Monto Input */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-2">
                Monto de tu meta
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-600">€</span>
                <input
                  type="number"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  placeholder="3000"
                  className="w-full pl-10 pr-4 py-3 text-xl font-bold border-3 border-gray-300 rounded-xl focus:border-[#fbb040] focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* Frecuencia de pago Slider */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-2">
                Frecuencia de pago: <span className="text-[#fbb040]">{frecuencia.charAt(0).toUpperCase() + frecuencia.slice(1)}</span>
              </label>
              <input
                type="range"
                min="0"
                max="3"
                value={['diario', 'semanal', 'quincenal', 'mensual'].indexOf(frecuencia)}
                onChange={(e) => {
                  const frecuencias = ['diario', 'semanal', 'quincenal', 'mensual'];
                  setFrecuencia(frecuencias[parseInt(e.target.value)]);
                }}
                className="w-full h-3 bg-gray-300 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #fbb040 0%, #fbb040 ${(['diario', 'semanal', 'quincenal', 'mensual'].indexOf(frecuencia) / 3) * 100}%, #d1d5db ${(['diario', 'semanal', 'quincenal', 'mensual'].indexOf(frecuencia) / 3) * 100}%, #d1d5db 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>Diario</span>
                <span>Semanal</span>
                <span>Quincenal</span>
                <span>Mensual</span>
              </div>
            </div>

            {/* Tiempo Slider */}
            <div>
              <label className="block text-base font-bold text-gray-700 mb-2">
                Tiempo: <span className="text-[#fbb040]">{tiempo} {tiempo === 1 ? 'mes' : 'meses'}</span>
              </label>
              <input
                type="range"
                min="0"
                max="6"
                value={[1, 3, 6, 12, 18, 24, 36].indexOf(tiempo)}
                onChange={(e) => {
                  const tiempos = [1, 3, 6, 12, 18, 24, 36];
                  setTiempo(tiempos[parseInt(e.target.value)]);
                }}
                className="w-full h-3 bg-gray-300 rounded-lg appearance-none cursor-pointer slider-thumb"
                style={{
                  background: `linear-gradient(to right, #fbb040 0%, #fbb040 ${([1, 3, 6, 12, 18, 24, 36].indexOf(tiempo) / 6) * 100}%, #d1d5db ${([1, 3, 6, 12, 18, 24, 36].indexOf(tiempo) / 6) * 100}%, #d1d5db 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>1</span>
                <span>3</span>
                <span>6</span>
                <span>12</span>
                <span>18</span>
                <span>24</span>
                <span>36</span>
              </div>
            </div>

            {/* Summary */}
            {monto && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-r from-[#fbb040]/20 to-yellow-100 p-4 rounded-xl border-3 border-[#fbb040]"
              >
                <p className="text-center text-base font-bold text-gray-700">
                  Aporte estimado ({frecuencia}):{' '}
                  <span className="text-2xl text-black block mt-1">
                    €{(() => {
                      const comisionPorcentaje = tiempo <= 6 ? 0.015 : 0.01;
                      const totalAPagar = parseFloat(monto) * (1 + tiempo * comisionPorcentaje);
                      const periodosPorMes = frecuencia === 'diario' ? 30 : frecuencia === 'semanal' ? 4 : frecuencia === 'quincenal' ? 2 : 1;
                      const aporteTotal = totalAPagar / (tiempo * periodosPorMes);
                      return aporteTotal.toFixed(2);
                    })()}
                  </span>
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Título antes de las opciones de ahorro */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center my-16"
        >
          <h3 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-orange-500 leading-tight pb-2">
            Dos caminos para activar tu meta
          </h3>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Ahorro Total */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -10 }}
            className="group"
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-orange-400 hover:border-orange-500 transition-all duration-300 h-full">
              <div className="bg-gradient-to-r from-orange-400 to-orange-600 text-white p-8">
                <h3 className="text-3xl font-black mb-2">Ahorro Total</h3>
                <p className="text-orange-50 text-lg">Konstruyes con Kompa</p>
              </div>
              <div className="p-8 space-y-6">
                <p className="text-gray-700 text-lg leading-relaxed">
                  Aportas hasta llegar al <span className="font-bold">100%</span>. Al completarlo <span className="font-black text-orange-500">Kompa te regresa hasta el 15% del valor de tu plan*</span>.
                </p>
                
                <div className="space-y-4">
                  {[
                    'Control total sin depender de grupos',
                    'Recibes hasta el 15% mas del valor de tu plan',
                    'Indexado y resguardado',
                    'Progreso a tu ritmo',
                  ].map((text, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="text-orange-500 flex-shrink-0 mt-1" size={24} />
                      <p className="text-gray-700 font-medium">{text}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200">
                  <p className="text-sm font-bold text-orange-600 mb-2">Ejemplo:</p>
                  <p className="text-gray-700 font-semibold">Plan de €3.000 a 36 meses</p>
                  <p className="text-orange-500 font-black text-lg mt-1">€450 Kompa te los suma</p>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-gradient-to-r from-orange-400 to-orange-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl"
                >
                  Elegir Ahorro Total
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Ahorra entre Kompas */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -10 }}
            className="group"
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-blue-600 hover:border-blue-700 transition-all duration-300 h-full">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
                <h3 className="text-3xl font-black mb-2">Ahorra entre Kompas</h3>
                <p className="text-blue-100 text-lg">Logra tu meta antes del 100%</p>
              </div>
              <div className="p-8 space-y-6">
                <p className="text-gray-700 text-lg leading-relaxed">
                  Al llegar al <span className="font-black text-blue-600">65%</span>, entras en grupos donde se seleccionan usuarios para liquidar su plan antes del 100%.
                </p>
                
                <div className="space-y-4">
                  {[
                    'Recibe tu meta desde el 65%',
                    'Selección automática por aportes',
                    'Indexado y resguardado',
                    'Continúas aportando después',
                  ].map((text, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                      <p className="text-gray-700 font-medium">{text}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                  <p className="text-sm font-bold text-blue-700 mb-2">Importante:</p>
                  <p className="text-gray-700 font-semibold">No es un crédito</p>
                  <p className="text-blue-600 font-black text-lg mt-1">Sin intereses bancarios</p>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl"
                >
                  Elegir Entre Kompas
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SavingsModalitiesSection;